/**
 * Каркас админки: боковое меню и шапка раздела.
 *
 * Сверху — кто ты и с какой ролью. В кабинете партнёра на этом месте стоит
 * бренд («за кого я действую»), здесь — сам сотрудник: в админке ты действуешь
 * от имени платформы, и это должно быть видно постоянно.
 */
import { Avatar, Badge, Button, Icon, Logo, ThemeToggle, type IconName } from '@allswipes/ui';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { useAdmin } from '../session';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end: boolean;
  /** Пункт только для тех, кто может менять данные. */
  moderator?: boolean;
  staffOnly?: boolean;
}

const GROUPS: Array<{ title?: string; items: NavItem[] }> = [
  {
    items: [
      { to: '/', label: 'Обзор', icon: 'dashboard', end: true },
      { to: '/brands', label: 'Бренды', icon: 'building', end: false },
      { to: '/company-requests', label: 'Заявки', icon: 'building', end: false },
      { to: '/campaigns', label: 'Акции', icon: 'list', end: false },
      { to: '/contests', label: 'Конкурсы', icon: 'star', end: false },
    ],
  },
  {
    title: 'Модерация',
    items: [
      { to: '/reviews', label: 'Отзывы', icon: 'star', end: false },
      { to: '/slides', label: 'Главная полоса', icon: 'flame', end: false, moderator: true },
      { to: '/spheres', label: 'Категории', icon: 'building', end: false },
    ],
  },
  {
    title: 'Платформа',
    items: [
      { to: '/staff', label: 'Сотрудники', icon: 'user', end: false, staffOnly: true },
      { to: '/visits', label: 'Посещения', icon: 'chart', end: false },
      { to: '/settings', label: 'Настройки', icon: 'settings', end: false, moderator: true },
      { to: '/audit', label: 'Журнал', icon: 'scroll', end: false },
    ],
  },
];

export function Layout({
  children,
  title,
  subtitle,
  action,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const { me, canModerate, canManageStaff, signOut } = useAdmin();
  const navigate = useNavigate();

  return (
    <div className="console">
      <aside className="side">
        <div className="ws" style={{ cursor: 'default' }}>
          <Logo height={16} />
          <span className="plan">админка</span>
        </div>

        {GROUPS.map((group, gi) => (
          <div key={group.title ?? gi}>
            {group.title && <div className="grp">{group.title}</div>}
            {group.items
              .filter((item) => (!item.moderator || canModerate) && (!item.staffOnly || canManageStaff))
              .map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end}>
                  <Icon name={item.icon} size={15} />
                  {item.label}
                </NavLink>
              ))}
          </div>
        ))}

        {me && (
          <div className="foot">
            <div className="row" style={{ gap: 7, padding: '4px 6px 8px' }}>
              <Avatar name={me.admin.name || me.admin.email} size={20} />
              <span
                className="faint"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={me.admin.email}
              >
                {me.admin.email}
              </span>
            </div>
            <div className="row" style={{ padding: '0 6px 8px' }}>
              <Badge tone={canModerate ? 'accent' : undefined}>
                {me.roles[me.admin.role] ?? me.admin.role}
              </Badge>
            </div>
            <div className="row">
              <ThemeToggle />
              <span className="spacer" />
              <Button
                variant="ghost"
                size="sm"
                icon="logout"
                title="Выйти"
                onClick={() => {
                  signOut();
                  navigate('/login');
                }}
              />
            </div>
          </div>
        )}
      </aside>

      <div className="content">
        <header className="chead">
          <div style={{ minWidth: 0 }}>
            <h1 className="h1">{title}</h1>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          <span className="spacer" />
          {action}
        </header>
        <div className="cbody">{children}</div>
      </div>
    </div>
  );
}

/**
 * Полоса «только смотришь».
 *
 * У поддержки нет права менять чужие данные, и кнопки ей показывать нечестно:
 * лучше сказать прямо, чем дать нажать и получить отказ сервера.
 */
export function ReadOnlyNote() {
  const { canModerate } = useAdmin();
  if (canModerate) return null;
  return (
    <div className="note" style={{ marginBottom: 14 }}>
      <Icon name="eye" size={14} />
      <span>У роли «Поддержка» доступ только на чтение — менять ничего нельзя.</span>
    </div>
  );
}
